import type Dexie from 'dexie';

import { getDb } from './LocalDB';

export const getStandardItemHandler = <T extends { id: string; deleted: boolean; _version?: number | null }>(
    table: Dexie.Table,
    transform?: (input: any) => any
): ((items: T[]) => Promise<void>) => {
    return async (items: T[]) => {
        // The change feed returns every version of a record since the last sync, so a batch can hold
        // the same id twice; writing two versions of one record in parallel lets an older one win.
        const latestById = new Map<string, T>();
        for (const item of items) {
            const kept = latestById.get(item.id);
            if (!kept || (item._version ?? 0) >= (kept._version ?? 0)) {
                latestById.set(item.id, item);
            }
        }
        const deduped = [...latestById.values()];
        const db = getDb();
        // One transaction with syncItems, so a markToSync cannot slip in between the check and the write.
        await db.transaction('rw', table, db.syncItems, async () => {
            // A row whose upload is still queued holds changes the server has not seen, and the queued write
            // brings the merged row back, so the incoming copy - deleted or not - must not replace it.
            const queued = await db.syncItems.bulkGet(deduped.map((i) => `${table.name}:${i.id}`));
            const incoming = deduped.filter((_i, index) => !queued[index]);
            const toDelete = incoming.filter((i) => i.deleted);
            let toPut = incoming.filter((i) => !i.deleted);
            if (transform) {
                toPut = toPut.map(transform);
            }
            await Promise.all([table.bulkDelete(toDelete.map((i) => i.id)), table.bulkPut(toPut)]);
        });
    };
};
